/**
 * 通用日期格式化函数 (短格式)
 * @param {string} dateString - 日期字符串
 * @returns {string} - 格式化后的日期
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    // Handle timestamp format by removing time part if it exists
    let cleanDate = dateString;
    if (typeof dateString === 'string' && dateString.includes('T')) {
      cleanDate = dateString.split('T')[0]; // Keep only the date part (YYYY-MM-DD)
    }
    
    const date = new Date(cleanDate);
    if (isNaN(date.getTime())) {
      // Try parsing the original string if cleaning failed
      const originalDate = new Date(dateString);
      if (isNaN(originalDate.getTime())) {
        return dateString; // Return original if still can't parse
      }
      return originalDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * 长格式日期格式化函数
 * @param {string} dateString - 日期字符串
 * @returns {string} - 格式化后的日期
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// 保持向后兼容
export const formatArticleDate = formatDate;

/**
 * 将文本截断为指定长度，并添加省略号
 * @param {string} text - 要截断的文本
 * @param {number} maxLength - 最大长度
 * @returns {string} - 截断后的文本
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * 从URL中获取查询参数
 * @param {string} param - 参数名
 * @returns {string} - 参数值
 */
export const getQueryParam = (param) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

/**
 * 从HTML或Markdown文本中提取纯文本摘要
 * @param {string} content - HTML或Markdown格式的内容
 * @param {number} maxLength - 摘要最大长度
 * @returns {string} - 提取的纯文本摘要
 */
export const extractExcerpt = (content, maxLength = 160) => {
  if (!content) return '';
  
  // 移除HTML标签
  let plainText = content.replace(/<[^>]+>/g, '');
  
  // 移除Markdown标记
  plainText = plainText
    .replace(/#{1,6}\s+/g, '') // 移除标题标记
    .replace(/\*\*|\*|~~|_|`/g, '') // 移除强调标记(粗体、斜体、删除线等)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 将链接替换为链接文本
    .replace(/!\[([^\]]+)\]\([^)]+\)/g, '$1') // 将图片替换为替代文本
    .replace(/\n+/g, ' ') // 将换行替换为空格
    .replace(/\s+/g, ' ') // 合并多个空格
    .trim(); // 移除首尾空格
  
  // 截断文本并添加省略号
  return plainText.length > maxLength 
    ? plainText.substring(0, maxLength - 3) + '...' 
    : plainText;
};

/**
 * 将Markdown文本转换为HTML
 * @param {string} markdown - Markdown文本
 * @returns {string} - HTML文本
 */
export const markdownToHTML = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown;
  
  // 预处理：修复错误的标题格式，如 ##### 标题 (带多余空格的标题格式)
  html = html
    // 修复常见的带有额外空格的标题格式
    .replace(/^(#{1,6})\s+(\d+\.\s+.*)$/gm, '$1 $2')
    .replace(/^(#{5})\s+(.*)$/gm, '##### $2');
  
  // Process markdown in the correct order
  html = html
    // Handle headers first with consistent styling and gray color
    .replace(/^# (.*?)$/gm, '<h1 class="text-3xl font-bold mb-6 mt-8 text-gray-800 border-b border-gray-200 pb-3 font-montserrat">$1</h1>')
    .replace(/^## (.*?)$/gm, '<h2 class="text-2xl font-bold mb-5 mt-7 text-gray-800 font-montserrat">$1</h2>')
    .replace(/^### (.*?)$/gm, '<h3 class="text-xl font-bold mb-4 mt-6 text-gray-800 font-montserrat">$1</h3>')
    .replace(/^#### (.*?)$/gm, '<h4 class="text-lg font-bold mb-3 mt-5 text-gray-800 font-montserrat">$1</h4>')
    .replace(/^##### (.*?)$/gm, '<h5 class="text-base font-bold mb-3 mt-4 text-gray-800 font-montserrat">$1</h5>')
    .replace(/^###### (.*?)$/gm, '<h6 class="text-sm font-bold mb-2 mt-3 text-gray-800 font-montserrat">$1</h6>')
    
    // Handle horizontal rules with neutral styling
    .replace(/^---$/gm, '<hr class="my-8 border-0 border-t border-gray-200" />')
    
    // Handle text formatting with consistent gray color
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-800">$1</strong>') // Bold in gray
    .replace(/\*(.*?)\*/g, '<em class="italic text-gray-800">$1</em>') // Italic in gray
    
    // Handle links with gray color and improved hover effect
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-orange-600 hover:text-orange-800 underline transition-colors duration-200">$1</a>')
    
    // Process code blocks
    .replace(/```(.*?)```/gs, '<pre class="bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto"><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm">$1</code>');
  
  // 处理可能的标题行但没有正确格式化的文本
  html = html.replace(/^([#]+)\s+(.*?\))$/gm, (match, hashes, text) => {
    // 移除末尾的右括号
    const cleanText = text.replace(/\)$/, '');
    const level = hashes.length;
    const className = 
      level === 1 ? 'text-3xl font-bold mb-6 mt-8 text-gray-800 border-b border-gray-200 pb-3 font-montserrat' :
      level === 2 ? 'text-2xl font-bold mb-5 mt-7 text-gray-800 font-montserrat' :
      level === 3 ? 'text-xl font-bold mb-4 mt-6 text-gray-800 font-montserrat' :
      level === 4 ? 'text-lg font-bold mb-3 mt-5 text-gray-800 font-montserrat' :
      level === 5 ? 'text-base font-bold mb-3 mt-4 text-gray-800 font-montserrat' :
                    'text-sm font-bold mb-2 mt-3 text-gray-800 font-montserrat';
    
    return `<h${level} class="${className}">${cleanText}</h${level}>`;
  });

  // Process unordered lists more reliably
  const processUnorderedLists = (text) => {
    // Find contiguous blocks of list items
    const listRegex = /^- (.*?)(?:\n(?:- .*?))*$/gm;
    return text.replace(listRegex, (match) => {
      const items = match.split('\n').map(item => 
        `<li class="ml-4 mb-2 text-gray-800">${item.replace(/^- /, '')}</li>`
      ).join('\n');
      return `<ul class="list-disc mb-6 mt-4 pl-6">${items}</ul>`;
    });
  };
  
  html = processUnorderedLists(html);
  
  // Process ordered lists more reliably
  const processOrderedLists = (text) => {
    // Find contiguous blocks of numbered list items
    const listRegex = /^(\d+)\. (.*?)(?:\n(?:\d+\. .*?))*$/gm;
    return text.replace(listRegex, (match) => {
      const items = match.split('\n').map(item => {
        const content = item.replace(/^\d+\. /, '');
        return `<li class="ml-4 mb-2 text-gray-800">${content}</li>`;
      }).join('\n');
      return `<ol class="list-decimal mb-6 mt-4 pl-6">${items}</ol>`;
    });
  };
  
  html = processOrderedLists(html);
  
  // 处理额外的空行和空格
  html = html.replace(/\n\s+\n/g, '\n\n').replace(/\n{3,}/g, '\n\n');
  
  // Handle paragraphs and line breaks with better spacing
  html = html.replace(/\n\n/g, '</p><p class="mb-5 text-gray-800 leading-relaxed">');
  
  // Wrap with paragraph tags if not already wrapped
  if (!html.startsWith('<h1') && !html.startsWith('<h2') && 
      !html.startsWith('<h3') && !html.startsWith('<h4') && 
      !html.startsWith('<h5') && !html.startsWith('<h6') && 
      !html.startsWith('<p') && !html.startsWith('<ul') && !html.startsWith('<ol')) {
    html = '<p class="mb-5 text-gray-800 leading-relaxed">' + html + '</p>';
  }
  
  // Fix spacing and alignment issues
  html = html
    .replace(/<\/h(\d)>\s*<p>/g, '</h$1><p>')
    .replace(/<\/p>\s*<h/g, '</p><h')
    .replace(/<p><hr class="my-8 border-0 border-t border-gray-200" \/><\/p>/g, '<hr class="my-8 border-0 border-t border-gray-200" />')
    .replace(/<p><\/p>/g, '')
    .replace(/<\/ul><p class="mb-5 text-gray-800 leading-relaxed">/g, '</ul>')
    .replace(/<p class="mb-5 text-gray-800 leading-relaxed"><ul/g, '<ul')
    .replace(/<\/ol><p class="mb-5 text-gray-800 leading-relaxed">/g, '</ol>')
    .replace(/<p class="mb-5 text-gray-800 leading-relaxed"><ol/g, '<ol')
    .replace(/<img/g, '<img class="max-w-full h-auto mx-auto rounded-lg my-6"');
  
  return html;
};

// Function to get proper category display name
export const getCategoryName = (category) => {
  if (!category) return 'Uncategorized';
  
  // Handle the exact category names from the data
  const categoryMap = {
    'hidden-destinations': 'Hidden Destinations',
    'Hidden Destinations': 'Hidden Destinations',
    
    'adventure-escapes': 'Adventure Escapes', 
    'Adventure Escapes': 'Adventure Escapes',
    
    'cultural-experiences': 'Cultural Experiences',
    'Cultural Experiences': 'Cultural Experiences',
    
    'travel-tips': 'Travel Tips',
    'Travel Tips': 'Travel Tips'
  };
  
  return categoryMap[category] || category;
};

// Get category text color class for headings
export const getCategoryTextClass = (category) => {
  const categoryName = getCategoryName(category);
  
  switch (categoryName) {
    case 'Hidden Destinations':
      return 'text-purple-600';
    case 'Adventure Escapes':
      return 'text-red-600';
    case 'Cultural Experiences':
      return 'text-blue-600';
    case 'Travel Tips':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

// Get hover color for category links/text
export const getCategoryHoverClass = (category) => {
  const categoryName = getCategoryName(category);
  
  switch (categoryName) {
    case 'Hidden Destinations':
      return 'hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300';
    case 'Adventure Escapes':
      return 'hover:bg-red-50 hover:text-red-700 hover:border-red-300';
    case 'Cultural Experiences':
      return 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300';
    case 'Travel Tips':
      return 'hover:bg-green-50 hover:text-green-700 hover:border-green-300';
    default:
      return 'hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300';
  }
};

// Get category badge color classes
export const getCategoryBadgeClass = (category) => {
  const categoryName = getCategoryName(category);
  
  switch (categoryName) {
    case 'Hidden Destinations':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Adventure Escapes':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Cultural Experiences':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Travel Tips':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Get category header class for header navigation
export const getCategoryHeaderClass = (category) => {
  const categoryName = getCategoryName(category);
  
  switch (categoryName) {
    case 'Hidden Destinations':
      return 'text-purple-300 hover:text-purple-100 bg-purple-600/10 hover:bg-purple-600/20';
    case 'Adventure Escapes':
      return 'text-red-300 hover:text-red-100 bg-red-600/10 hover:bg-red-600/20';
    case 'Cultural Experiences':
      return 'text-blue-300 hover:text-blue-100 bg-blue-600/10 hover:bg-blue-600/20';
    case 'Travel Tips':
      return 'text-green-300 hover:text-green-100 bg-green-600/10 hover:bg-green-600/20';
    default:
      return 'text-gray-300 hover:text-gray-100';
  }
};

// Get category slug from name
export const getCategorySlug = (categoryName) => {
  const slugMap = {
    'Hidden Destinations': 'hidden-destinations',
    'Adventure Escapes': 'adventure-escapes',
    'Cultural Experiences': 'cultural-experiences',
    'Travel Tips': 'travel-tips'
  };
  return slugMap[categoryName] || categoryName.toLowerCase().replace(/\s+/g, '-');
};

// Get category CSS classes for general card styling
export const getCategoryClass = (category) => {
  const categoryName = getCategoryName(category);
  
  switch (categoryName) {
    case 'Hidden Destinations':
      return 'category-hidden-destinations';
    case 'Adventure Escapes':
      return 'category-adventure-escapes';
    case 'Cultural Experiences':
      return 'category-cultural-experiences';
    case 'Travel Tips':
      return 'category-travel-tips';
    default:
      return 'category-general';
  }
};

// Backward compatibility - alias for getCategoryTextClass
export const getCategoryColorClass = getCategoryTextClass;

// Get short category name for display
export const getShortCategoryName = (category) => {
  const categoryName = getCategoryName(category);
  const nameMap = {
    'Hidden Destinations': 'Hidden',
    'Adventure Escapes': 'Adventure',
    'Cultural Experiences': 'Cultural',
    'Travel Tips': 'Tips'
  };
  return nameMap[categoryName] || categoryName;
}; 